import React, { Children } from 'react';
import { FlexCenterRow } from '../common/forms/FormElements';
import { withRouter } from 'react-router-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import './AssignmentCalendar.scss';
import moment from 'moment';
import authService from '../api-authorization/AuthorizeService';
import { util } from '../Util';
import { BaseFormViewModel } from '../common/ViewModel';
import CommonContext from '../Common';
import { MomentDateFormat } from '../MomentDateFormat';
import { ApiRoutes } from '../ApiRoutes';
import { AppNavPaths } from '../AppNavPaths';
import {
    AssignmentToolbar,
    AssignmentToolbarNavigation,
} from './AssignmentToolbar';
import { AssignmentEvent } from './AssignmentEvent';
import { views } from './CalendarViews';
import { LinearProgress } from '@mui/material';
import { TimesheetStatus } from '../timesheet/Timesheet';
import { getRange } from '../utilities/DateHelpers';
import { getTenantUserProfile } from '../common/TenantUserProfile';

const localizer = momentLocalizer(moment);

const CURRENT_DATE = moment();

const ColoredDateCellWrapper = ({ children, value }) =>
    React.cloneElement(Children.only(children), {
        style: {
            ...children.style,
            backgroundColor: CURRENT_DATE.isAfter(moment(value), 'day')
                ? '#ddd'
                : CURRENT_DATE.isSame(moment(value), 'day')
                  ? '#c0e1fa'
                  : 'white',
        },
    });

class AssignmentCalendar extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const stateBase = {
            loading: true,
            events: [], // list of calendar events
            user: null,
            calendarAssignments: [],
            view: 'month',
            defaultView: 'month',
            startDate: moment()
                .startOf('month')
                .format(MomentDateFormat.ApiUrlRequest),
            endDate: moment()
                .endOf('month')
                .format(MomentDateFormat.ApiUrlRequest),
            date: new Date(),
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    onClearErrors = () => this.setState((state) => ({ errors: {} }));

    onNavigate = async (date, view, action) => {
        if (action == AssignmentToolbarNavigation.ADD) {
            if (!date) {
                return;
            }

            const urlDate = moment(date).format(MomentDateFormat.ApiUrlRequest);

            this.props.history.push(
                `${AppNavPaths.DailyTimesheet}/date/${urlDate}`
            );

            return;
        }

        const currentDate = this.state.date;

        let newDate = currentDate;

        if (action == AssignmentToolbarNavigation.PREVIOUS) {
            newDate = moment(currentDate).add(-1, view);
        } else if (action == AssignmentToolbarNavigation.TODAY) {
            newDate = new Date();
        } else if (action == AssignmentToolbarNavigation.NEXT) {
            newDate = moment(currentDate).add(1, view);
        }

        newDate = moment(newDate).toDate();

        await this.setState({ date: newDate }, async () => {
            await this.getAssignments();
        });
    };

    toCalendarEventTitle = (calendarAssignment) =>
        moment(calendarAssignment.day).format('h:mma').slice(0, -1);

    getAssignments = async () => {
        await this.setState({ loading: true });

        const { date, view } = this.state;

        const start = moment(date)
            .startOf(view)
            .format(MomentDateFormat.ApiUrlRequest);

        const end = moment(date)
            .endOf(view)
            .format(MomentDateFormat.ApiUrlRequest);

        const [calendarAssignments, timesheetStatuses, dailyTimesheets] =
            await Promise.all([
                util.fetch.js(ApiRoutes.calendar.get(start, end)),
                util.fetch.js(ApiRoutes.calendar.timesheetStatuses(start, end)),
                util.fetch.js(
                    ApiRoutes.dailyTimesheets.getRangeFor(start, end)
                ),
            ]);

        // https://jquense.github.io/react-big-calendar/examples/index.html#prop-events
        const events = this.toCalendarAssignments(
            calendarAssignments,
            timesheetStatuses,
            dailyTimesheets
        ).concat(
            this.convertDailyTimesheetsToCalendarAssignments(
                dailyTimesheets,
                timesheetStatuses
            )
        );

        this.setState({
            events,
            loading: false,
            calendarAssignments,
            timesheetStatuses,
        });
    };

    setSaving = (saving) => this.setState({ saving });

    changeView = (view) => {
        this.setState({ view });
    };

    customEventPropGetter = (event) => {
        let statusClass = '';

        if (event.timesheetSubmitted) {
            statusClass = 'submitted';
        } else if (event.rejected) {
            statusClass = 'rejected';
        } else if (event.confirmed) {
            statusClass = 'confirmed';
        } else if (event.newDailyTimesheet) {
            statusClass = 'optional';
        } else {
            statusClass = 'unconfirmed';
        }

        return { className: `${this.state.view} ${statusClass}` };
    };

    handleEventSelect = (event, e) => {
        if (event.isLowboyAssignment) {
            const apiDate = moment(event.day).format(
                MomentDateFormat.ApiUrlRequest
            );

            this.props.history.push(
                `${AppNavPaths.LowboyAssignments}/${apiDate}`
            );
        } else {
            if (event.dailyTimesheetId > 0) {
                this.props.history.push(
                    `${AppNavPaths.DailyTimesheet}/id/${event.dailyTimesheetId}`
                );
            } else if (event.newDailyTimesheet) {
                const apiDate = moment(event.day).format(
                    MomentDateFormat.ApiUrlRequest
                );

                this.props.history.push(
                    `${AppNavPaths.DailyTimesheet}/date/${apiDate}`
                );
            } else {
                // confirm
                if (event.isCancellation) {
                    this.props.history.push(
                        `${AppNavPaths.CancelConfirmation}/${event.assignmentCancellationId}`
                    );
                } else {
                    this.props.history.push(
                        `${AppNavPaths.AssignmentConfirmation}/${event.assignmentId}`
                    );
                }
            }
        }
    };

    toCalendarAssignments(calendarAssignments, timesheetStatuses) {
        return calendarAssignments.map((ca) => {
            const ev = {
                title: this.toCalendarEventTitle(ca),
                start: moment(ca.day).toDate(),
                end: moment(ca.day).toDate(),
                allDay: false,
                resource: null,
                eventTimeRangeFormat: '',
                timesheetSubmitted: timesheetStatuses.some(
                    (ts) =>
                        (ts.timesheetStatusId >= TimesheetStatus.Submitted &&
                            ts.jobAssignmentIds.includes(ca.assignmentId)) ||
                        (ts.timesheetStatusId >= TimesheetStatus.Submitted &&
                            moment(ts.date).isSame(ca.day, 'day'))
                ),
            };

            return { ...ca, ...ev };
        });
    }

    fromDailyTimesheetToEvent = (dt) => {
        return {
            title: moment(dt.date).format(
                MomentDateFormat.DateOnlyUTCHyphenated
            ),
            confirmed: true,
            jobTitle: 'Timesheet',
            allDay: false,
            dailyTimesheetId: dt.id,
            timesheetSubmitted:
                dt.timesheetStatusId >= TimesheetStatus.Submitted &&
                dt.timesheetStatusId != TimesheetStatus.Rejected,
            rejected: dt.timesheetStatusId == TimesheetStatus.Rejected,
            day: dt.date,
            start: moment(dt.date).toDate(),
            end: moment(dt.date).toDate(),
        };
    };

    fromMomentToTimesheetEvent = (d) => {
        return {
            title: 'Add',
            jobTitle: 'Timesheet',
            day: d.toDate(),
            start: d.toDate(),
            end: d.toDate(),
            newDailyTimesheet: true,
        };
    };

    convertDailyTimesheetsToCalendarAssignments = (dailyTimesheets) => {
        const { view, date, canCreateDailyTimesheets } = this.state;

        if (canCreateDailyTimesheets) {
            const dateToUse = date || new Date();

            const rangeStart = moment(dateToUse).startOf(view);
            const rangeEnd = moment(dateToUse).endOf(view);

            const dateRange = getRange(rangeStart, rangeEnd, 'day');

            return dateRange.map((d) => {
                const dailyTimesheet = dailyTimesheets.find((dt) =>
                    moment(dt.date).isSame(d, 'day')
                );

                return dailyTimesheet
                    ? this.fromDailyTimesheetToEvent(dailyTimesheet)
                    : this.fromMomentToTimesheetEvent(d);
            });
        }

        if (!dailyTimesheets || !dailyTimesheets.length) {
            return [];
        }

        return dailyTimesheets.map((dt) => this.fromDailyTimesheetToEvent(dt));
    };

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();
        if (isAuthenticated) {
            const [userProfile] = await Promise.all([getTenantUserProfile()]);

            const { userPermissions } = userProfile;

            const canCreateDailyTimesheets =
                userPermissions &&
                userPermissions.includes('dailytimesheet.edit');

            this.setState({ canCreateDailyTimesheets }, async () => {
                await this.getAssignments();
            });
        }
    }

    render() {
        const { events, loading, date, view } = this.state;

        if (!(this.context ?? {}).user) {
            return null;
        }

        if (loading) {
            return <LinearProgress variant="indeterminate" color="secondary" />;
        }

        return (
            <div className="assignmentCalendarPage calendar-content p-1">
                <Calendar
                    selectable
                    toolbar
                    views={views}
                    view={view}
                    date={date}
                    localizer={localizer}
                    startAccessor="start"
                    endAccessor="end"
                    events={events}
                    showAllEvents
                    longPressThreshold={75}
                    onSelectEvent={this.handleEventSelect.bind(this)}
                    eventPropGetter={this.customEventPropGetter.bind(this)}
                    components={{
                        toolbar: (props) => (
                            <AssignmentToolbar {...props} date={date} />
                        ),
                        event: AssignmentEvent,
                        dateCellWrapper: ColoredDateCellWrapper,
                    }}
                    onView={this.changeView.bind(this)}
                    onNavigate={this.onNavigate}
                />
                <FlexCenterRow className="confirm-legend">
                    <small className="optional">Optional</small>
                    <small className="unconfirmed">Unconfirmed</small>
                    <small className="confirmed">Confirmed</small>
                    <small className="rejected ml-2">Time Rejected</small>
                    <small className="submitted ml-2">Submitted</small>
                </FlexCenterRow>
            </div>
        );
    }
}

export default withRouter(AssignmentCalendar);

